import CircleIcon from '@mui/icons-material/Circle';
import { Timeline, TimelineItem } from '@mui/lab';
import { Grid, Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  getUserById,
  getWorkspaceUserById,
  IInviteStatus,
  IWorkspace,
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsGlobalFilters,
  getRecruiterActivitLogs,
  ActivityLogDTO,
  getUserByEmail,
  getOverlapTeams,
} from 'wisestep-api';
import {
  formatDateInDayMonthYearTime,
  getFormatDate,
  IUserDTO,
  ScrollableButtonTabs,
  WsColors,
  WsPageHeader,
  WsTimeLine,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import AccessControl from '../../../../components/AccessControl/AccessControl';
import { NotesParent } from '../../../../components/communicationBar/Notes';
import PinDashboardCollectionButton from '../../../../components/pinDashboardCollectionButton';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { WsTabs } from '../../../../components/WsTabs';
import { useUserContext, useWorkspaceContext } from '../../../../context';
import { WsActivityProps } from '../../../../features/activityLog/ActivityHelper';
import ComposeNote from '../../../../features/clientDetails/components/notes/ComposeNote';
import {
  getDashBoardBreadCrumbList,
  IDashBoardBreadCrumbTypes,
} from '../../../../features/users/helper/defaultValues';
import { useFetchDeps } from '../../../../hooks/useFetchDeps';
import CircleUserLock from '../../../../images/content/CircleUserLock.svg';
import { CRUCIBLE_WORKSPACE_ID, MAIL_ART_WORKSPACE_ID } from '../../../../utils/Constants';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import NotificationSettingsPage from '../../../notificationSettings';
import RecruiterSettings from '../RecruiterSettings';
import SettingNewPassword from '../settingNewPassword/SettingNewPassword';

import { filteredTabNames } from './helper/profileHelper';
import UserProfileOverview from './UserProfileOverview';
import UserProfileOverviewTemp from './UserProfileOverviewTemp';

const Profile = () => {
  const location: any = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const loggedInUser = useUserContext().userDTO ?? null;
  const [params] = useSearchParams();
  const recruiterEmail = String(params.get('email') ?? '');
  const isLink: boolean = Boolean(params.get('isLink')) || false;
  const isUserAnalytics: boolean = Boolean(params.get('isUserAnalytics')) || false;
  const [recruiterId, setRecruiterId] = useState<string>('');
  const [isOverLap, setIsOverLap] = useState<string>('');
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [activityLogData, setActivityLogData] = useState<ActivityLogDTO[]>([]);
  const [activityLists, setActivityLists] = useState<WsActivityProps[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const userId = isLink ? recruiterId : queryParams.get('id') ?? loggedInUser?.id;
  const [id, setId] = useState<string | undefined>(userId);
  const [user, setUser] = useFetchDeps<IUserDTO | null>(
    ([id]) => (isUserAnalytics ? getUserByEmail(recruiterEmail) : getUserById(id)),
    [id],
    null
  );
  const [workspaceUser] = useFetchDeps<IWorkspace>(
    ([user]) => getWorkspaceUserById(user?.email),
    [user]
  );
  const [selectedOption, setSelectedOption] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = (await getUserByEmail(recruiterEmail))?.data;
        const currentUserTeams = await getOverlapTeams(recruiterEmail);
        const hasOverlap = currentUserTeams.data?.length > 0 ? true : false;
        user && setRecruiterId(user.id);
        setUser(user);
        setId(user.id);
        setIsOverLap(hasOverlap ? 'yes' : 'no');
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [recruiterEmail]);

  const getFilters = useMemo(() => {
    const filters = {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    filters[ComposableDashboardFiltersEnum.RECRUITER] = {
      recruiterNames: [user?.email ?? ''],
    };
    return filters;
  }, [user?.email]);
  const workspaceContext = useWorkspaceContext();
  const workspaceForNewProfileTaggingFlow = [
    CRUCIBLE_WORKSPACE_ID,
    MAIL_ART_WORKSPACE_ID,
    'c809aa49-c696-4ea1-8467-0108a16ab786',
  ];

  useEffect(() => {
    const fetchDataLog = async () => {
      try {
        if (!user?.email) {
          setActivityLogData([]);
          return;
        }
        const { data: activityResponseEntity } = await getRecruiterActivitLogs(user?.email ?? '');
        setActivityLogData(activityResponseEntity);
        const updatedActivityLists = activityResponseEntity.map((data) => ({
          activity: (
            <TimelineItem key={data.id}>
              <Grid container>
                <Grid item xs={12}>
                  <WsTypography color={WsTypoColors.TERTIARY}>
                    {` At ${formatDateInDayMonthYearTime(data.createdOn.toString())}`}
                  </WsTypography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant={WsTypoVariants.SUB_TITLE}>
                    {`${data.field} Category -> ${data.description} client ${data.parentEntitiyName}`}
                  </Typography>
                </Grid>
              </Grid>
            </TimelineItem>
          ),
          leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize="small" />,
        }));

        setActivityLists(updatedActivityLists);
      } catch {
        setNotification(failed(`Failed to fetch company Information`));
      }
    };
    fetchDataLog();
  }, [setNotification, user?.email]);

  return (
    <>
      {isUserAnalytics && (
        <WsBreadCrumbs
          breadCrumbsList={getDashBoardBreadCrumbList(
            IDashBoardBreadCrumbTypes.USERS,
            user?.firstName || user?.lastName ? `${user?.firstName} ${user?.lastName}` : 'User'
          )}
        />
      )}

      <WsPageHeader
        title={`${user?.firstName} ${user?.lastName}`}
        badgeLable={
          workspaceUser?.barred
            ? IInviteStatus.DEACTIVATED
            : workspaceUser?.accepted
            ? IInviteStatus.ACTIVATED
            : IInviteStatus.INVITED
        }
        subtitle={`Profile created ovn ${getFormatDate(
          workspaceUser?.requestedOn?.toString() ?? ''
        )}`}
        rightChildren={
          <PinDashboardCollectionButton
            filters={getFilters}
            entityId={user?.email ?? ''}
            entityName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`?.trim()}
            fromProfile={true}
          />
        }
        text={workspaceUser?.role ?? ''}
        socialUrl={user?.linkedInProfileLink ?? ''}
        profileImg={{ profileImage: 'user.profileImage', required: true }}
        phoneNumbers={[user?.phoneNumber ?? '']}
      />
      <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
        <ScrollableButtonTabs
          scrollList={filteredTabNames(isLink)}
          getTabIndex={(index: number) => setTabIndex(index)}
          defaultValue={tabIndex}
        />
      </Grid>
      {tabIndex == 0 && (
        <>
          {isOverLap == 'yes' ? (
            workspaceForNewProfileTaggingFlow.includes(workspaceContext?.id as string) ? (
              <UserProfileOverview
                recruiterId={!isUserAnalytics ? user?.email : undefined}
                userId={isUserAnalytics ? recruiterEmail : undefined}
              />
            ) : (
              <UserProfileOverviewTemp userId={isUserAnalytics ? recruiterEmail : undefined} />
            )
          ) : (
            <Grid item xs={8.5}>
              <AccessControl
                errorText="You cannot access recruiters data as the user is not part of any mutual teams"
                icon={<img src={CircleUserLock} alt="No Data Available" />}
                hideNavigation={true}
              />
            </Grid>
          )}
        </>
      )}
      {tabIndex == 1 && !isLink && (
        <Grid container rowGap={3}>
          <Grid item xs={3}>
            <RecruiterSettings getSelectedOption={setSelectedOption} />
          </Grid>
          <Grid item xs={9} pt={3}>
            {selectedOption === 0 ? <NotificationSettingsPage /> : <SettingNewPassword />}
          </Grid>
        </Grid>
      )}
      {(isLink ? tabIndex == 1 : tabIndex == 2) && (
        <WsTabs>
          <WsTabs.Item label="Notes">
            <ComposeNote
              parent={NotesParent.USERPROFILE}
              parentId={''}
              isClient={true}
              email={user?.email ?? ''}
            />
          </WsTabs.Item>
          <WsTabs.Item label="Activities">
            {activityLogData && (
              <Grid item xs={12} borderRadius={2} pl={2} sx={{ background: WsColors.White }}>
                <Timeline>
                  <Box ml="-100%">
                    <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                  </Box>
                </Timeline>
              </Grid>
            )}
          </WsTabs.Item>
        </WsTabs>
      )}
    </>
  );
};

export default Profile;
